import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  ComposedChart,
  Scatter,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceArea,
} from "recharts";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import heatwaveSummaryCsv from "../../../data/HeatwaveJune2026/heatwave_summary_by_country_date.csv";
import HeatwaveCountryFilter from "./HeatwaveCountryFilter";
import {
  ChartCountry,
  CHART_COUNTRIES,
  EXCLUDED_DATES,
  parseHeatwaveSummaryCsv,
  roundDownTo5,
  roundUpTo5,
  secondsToMinutes,
  SummaryRow,
} from "./heatwaveChartShared";

const colors = {
  textDark: "#0f2744",
  textMid: "#2d4a6a",
  textMuted: "#5a7a94",
  cardBorder: "rgba(255, 255, 255, 0.95)",
  skyDeep: "#4A9FD4",
  trendline: "#DC2626",
};

const HEAT_THRESHOLD_C = 30;
const HEAT_ZONE_GRADIENT_ID = "heatwave-scatter-heat-zone";
const DEFAULT_Y_DOMAIN: [number, number] = [0, 8];

const ChartWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 22rem;
  min-height: 22rem;

  @media (min-width: 768px) {
    height: 26rem;
    min-height: 26rem;
  }
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
  font-size: 0.875rem;
  color: ${colors.textMuted};
`;

const LegendLine = styled.span`
  display: inline-block;
  width: 2rem;
  height: 3px;
  background: ${colors.trendline};
  border-radius: 2px;
`;

const TooltipCard = styled.div`
  background: rgba(255, 255, 255, 0.97);
  border: 1px solid ${colors.cardBorder};
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  box-shadow: 0 8px 20px rgba(74, 159, 212, 0.2);
  font-size: 0.875rem;
  color: ${colors.textDark};
  text-align: left;
  min-width: 10rem;
`;

const FloatingTooltipShell = styled.div<{ $x: number; $y: number }>`
  position: absolute;
  left: ${({ $x }) => $x}px;
  top: ${({ $y }) => $y}px;
  transform: translate(-50%, calc(-100% - 12px));
  pointer-events: none;
  z-index: 30;
`;

const FloatingTooltipCard = styled(TooltipCard)`
  animation: tooltipPopFade 0.15s ease-out;

  @keyframes tooltipPopFade {
    from {
      opacity: 0;
      transform: scale(0.94);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;

  &:not(:last-child) {
    margin-bottom: 0.35rem;
  }

  span:first-child {
    color: ${colors.textMuted};
  }

  span:last-child {
    font-weight: 600;
    color: ${colors.textDark};
  }
`;

const DOT_RADIUS = 6;

export type ScatterPoint = {
  serviceDate: string;
  temperature: number;
  value: number;
  fill: string;
};

function solve3x3(matrix: number[][], vector: number[]): [number, number, number] | null {
  const a = matrix.map((row) => [...row]);
  const b = [...vector];

  for (let col = 0; col < 3; col++) {
    let pivot = col;
    for (let row = col + 1; row < 3; row++) {
      if (Math.abs(a[row][col]) > Math.abs(a[pivot][col])) {
        pivot = row;
      }
    }
    if (Math.abs(a[pivot][col]) < 1e-12) return null;
    [a[col], a[pivot]] = [a[pivot], a[col]];
    [b[col], b[pivot]] = [b[pivot], b[col]];

    for (let row = col + 1; row < 3; row++) {
      const factor = a[row][col] / a[col][col];
      for (let j = col; j < 3; j++) a[row][j] -= factor * a[col][j];
      b[row] -= factor * b[col];
    }
  }

  const result = [0, 0, 0];
  for (let row = 2; row >= 0; row--) {
    let sum = b[row];
    for (let col = row + 1; col < 3; col++) {
      sum -= a[row][col] * result[col];
    }
    result[row] = sum / a[row][row];
  }
  return result as [number, number, number];
}

function fitQuadratic(points: { temperature: number; value: number }[]) {
  let s0 = 0;
  let s1 = 0;
  let s2 = 0;
  let s3 = 0;
  let s4 = 0;
  let t0 = 0;
  let t1 = 0;
  let t2 = 0;

  for (const { temperature: x, value: y } of points) {
    const x2 = x * x;
    const x3 = x2 * x;
    const x4 = x3 * x;
    s0 += 1;
    s1 += x;
    s2 += x2;
    s3 += x3;
    s4 += x4;
    t0 += y;
    t1 += x * y;
    t2 += x2 * y;
  }

  const coeffs = solve3x3(
    [
      [s0, s1, s2],
      [s1, s2, s3],
      [s2, s3, s4],
    ],
    [t0, t1, t2]
  );

  if (!coeffs) return { a: 0, b: 0, c: 0 };
  const [c, b, a] = coeffs;
  return { a, b, c };
}

function quadraticR2(points: { temperature: number; value: number }[]): number | null {
  if (points.length < 3) return null;

  const { a, b, c } = fitQuadratic(points);
  const meanY = points.reduce((sum, p) => sum + p.value, 0) / points.length;

  let ssRes = 0;
  let ssTot = 0;
  for (const { temperature: x, value: y } of points) {
    const predicted = a * x * x + b * x + c;
    ssRes += (y - predicted) ** 2;
    ssTot += (y - meanY) ** 2;
  }

  if (ssTot < 1e-12) return null;
  return Math.max(0, 1 - ssRes / ssTot);
}

function heatColor(heat: number): string {
  const t = Math.max(0, Math.min(1, heat));
  const r = Math.round(250 + t * (239 - 250));
  const g = Math.round(204 + t * (68 - 204));
  const b = Math.round(21 + t * (68 - 21));
  return `rgb(${r}, ${g}, ${b})`;
}

function formatDelayMinutes(minutes: number): string {
  return `${minutes.toFixed(1)} min`;
}

function formatServiceDate(dateStr: string): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  return dayjs(new Date(year, month - 1, day)).format("D MMM YYYY");
}

function buildScatterPoints(rows: SummaryRow[]): ScatterPoint[] {
  const minT = Math.min(...rows.map((r) => r.max_temperature_c));
  const maxT = Math.max(...rows.map((r) => r.max_temperature_c));
  const delays = rows.map((r) => secondsToMinutes(r.avg_delay_s));
  const minY = Math.min(...delays);
  const maxY = Math.max(...delays);

  return rows.map((row) => {
    const value = secondsToMinutes(row.avg_delay_s);
    const normX = (row.max_temperature_c - minT) / (maxT - minT || 1);
    const normY = (value - minY) / (maxY - minY || 1);
    const heat = (normX + normY) / 2;

    return {
      serviceDate: row.date,
      temperature: row.max_temperature_c,
      value,
      fill: heatColor(heat),
    };
  });
}

function buildTrendline(points: ScatterPoint[], steps = 80) {
  const { a, b, c } = fitQuadratic(points);
  const minT = Math.min(...points.map((p) => p.temperature));
  const maxT = Math.max(...points.map((p) => p.temperature));

  return Array.from({ length: steps }, (_, i) => {
    const temperature = minT + (i / (steps - 1)) * (maxT - minT);
    return {
      temperature,
      value: a * temperature * temperature + b * temperature + c,
    };
  });
}

function computeYDomain(points: ScatterPoint[]): [number, number] {
  if (points.length === 0) return DEFAULT_Y_DOMAIN;

  const maxValue = Math.max(...points.map((p) => p.value));
  const paddedMax = maxValue * 1.12;
  return [0, Math.max(4, Math.ceil(paddedMax))];
}

function computeTemperatureAxis(rows: SummaryRow[]): { domain: [number, number]; ticks: number[] } {
  const fallback = { domain: [10, 35] as [number, number], ticks: [10, 15, 20, 25, 30, 35] };
  if (rows.length === 0) return fallback;

  const temps = rows.map((row) => row.max_temperature_c);
  let min = roundDownTo5(Math.min(...temps));
  let max = roundUpTo5(Math.max(...temps));
  if (max <= min) max = min + 5;

  const ticks: number[] = [];
  for (let tick = min; tick <= max; tick += 5) {
    ticks.push(tick);
  }

  return { domain: [min, max], ticks };
}

type HoveredDot = {
  point: ScatterPoint;
  x: number;
  y: number;
};

const PointTooltipContent = ({ point }: { point: ScatterPoint }) => {
  const { t } = useTranslation();

  return (
    <>
      <TooltipRow>
        <span>{t("heatwave.perDegree.scatterTooltip.date")}</span>
        <span>{formatServiceDate(point.serviceDate)}</span>
      </TooltipRow>
      <TooltipRow>
        <span>{t("heatwave.perDegree.scatterTooltip.temperature")}</span>
        <span>{point.temperature.toFixed(1)}°C</span>
      </TooltipRow>
      <TooltipRow>
        <span>{t("heatwave.perDegree.scatterTooltip.delay")}</span>
        <span>{formatDelayMinutes(point.value)}</span>
      </TooltipRow>
    </>
  );
};

const ScatterDot = ({
  cx,
  cy,
  payload,
  onHover,
  onLeave,
}: {
  cx?: number;
  cy?: number;
  payload?: ScatterPoint;
  onHover: (point: ScatterPoint, cx: number, cy: number) => void;
  onLeave: () => void;
}) => {
  if (cx == null || cy == null || !payload) return null;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={DOT_RADIUS}
      fill={payload.fill}
      stroke="rgba(255, 255, 255, 0.9)"
      strokeWidth={1.5}
      style={{ cursor: "pointer" }}
      onMouseEnter={() => onHover(payload, cx, cy)}
      onMouseLeave={onLeave}
    />
  );
};

interface TemperatureDelayScatterProps {
  visible?: boolean;
}

const TemperatureDelayScatter: React.FC<TemperatureDelayScatterProps> = ({ visible = true }) => {
  const { t, i18n } = useTranslation();
  const chartRef = useRef<HTMLDivElement>(null);
  const [allRows, setAllRows] = useState<SummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState<ChartCountry>("ALL");
  const [hoveredDot, setHoveredDot] = useState<HoveredDot | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetch(heatwaveSummaryCsv);
        if (!response.ok) {
          setAllRows([]);
          return;
        }
        const text = await response.text();
        const parsed = parseHeatwaveSummaryCsv(text).filter(
          (row) =>
            !EXCLUDED_DATES.has(row.date) &&
            !isNaN(row.avg_delay_s) &&
            !isNaN(row.max_temperature_c)
        );
        setAllRows(parsed);
      } catch {
        setAllRows([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const availableCountries = useMemo(() => {
    const codes = new Set(allRows.map((row) => row.stopcountry));
    return CHART_COUNTRIES.filter((code) => codes.has(code));
  }, [allRows]);

  const rows = useMemo(
    () => allRows.filter((row) => row.stopcountry === country),
    [allRows, country]
  );

  const scatterData = useMemo(() => buildScatterPoints(rows), [rows]);
  const trendlineData = useMemo(
    () => (scatterData.length > 2 ? buildTrendline(scatterData) : []),
    [scatterData]
  );
  const trendlineR2 = useMemo(() => quadraticR2(scatterData), [scatterData]);
  const yDomain = useMemo(() => computeYDomain(scatterData), [scatterData]);
  const { domain: temperatureDomain, ticks: temperatureTicks } = useMemo(
    () => computeTemperatureAxis(rows),
    [rows]
  );
  const heatZoneStart = Math.max(HEAT_THRESHOLD_C, temperatureDomain[0]);

  useEffect(() => {
    setHoveredDot(null);
  }, [country, scatterData]);

  const updateTooltipPosition = useCallback((point: ScatterPoint, cx: number, cy: number) => {
    const svg = chartRef.current?.querySelector(".recharts-surface");
    if (!(svg instanceof SVGSVGElement)) return;

    const containerRect = chartRef.current?.getBoundingClientRect();
    if (!containerRect) return;

    const ctm = svg.getScreenCTM();
    if (!ctm) return;

    const screenX = cx * ctm.a + cy * ctm.c + ctm.e;
    const screenY = cx * ctm.b + cy * ctm.d + ctm.f;

    setHoveredDot({
      point,
      x: screenX - containerRect.left,
      y: screenY - containerRect.top,
    });
  }, []);

  const handleDotHover = useCallback(
    (point: ScatterPoint, cx: number, cy: number) => {
      updateTooltipPosition(point, cx, cy);
    },
    [updateTooltipPosition]
  );

  const handleDotLeave = useCallback(() => {
    setHoveredDot(null);
  }, []);

  const renderScatterDot = useCallback(
    (props: { cx?: number; cy?: number; payload?: ScatterPoint }) => (
      <ScatterDot {...props} onHover={handleDotHover} onLeave={handleDotLeave} />
    ),
    [handleDotHover, handleDotLeave]
  );

  if (loading) {
    return (
      <ChartWrapper style={{ display: "flex", alignItems: "center", justifyContent: "center", color: colors.textMuted }}>
        {t("heatwave.perDegree.loading")}
      </ChartWrapper>
    );
  }

  if (!allRows.length) {
    return (
      <ChartWrapper style={{ display: "flex", alignItems: "center", justifyContent: "center", color: colors.textMuted }}>
        {t("heatwave.perDegree.loadError")}
      </ChartWrapper>
    );
  }

  return (
    <div style={{ opacity: visible ? 1 : 0.4, transition: "opacity 0.8s ease" }}>
      <HeatwaveCountryFilter
        country={country}
        onChange={setCountry}
        availableCountries={availableCountries}
      />
      <ChartWrapper ref={chartRef}>
        {rows.length === 0 ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: colors.textMuted,
            }}
          >
            {t("heatwave.perDegree.loadError")}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart key={country} margin={{ top: 12, right: 20, bottom: 36, left: 8 }}>
              <defs>
                <linearGradient id={HEAT_ZONE_GRADIENT_ID} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#FFB74D" stopOpacity={0.1} />
                  <stop offset="100%" stopColor="#FF7043" stopOpacity={0.22} />
                </linearGradient>
              </defs>
              {heatZoneStart < temperatureDomain[1] && (
                <ReferenceArea
                  x1={heatZoneStart}
                  x2={temperatureDomain[1]}
                  y1={yDomain[0]}
                  y2={yDomain[1]}
                  fill={`url(#${HEAT_ZONE_GRADIENT_ID})`}
                  strokeOpacity={0}
                  ifOverflow="hidden"
                  style={{ pointerEvents: "none" }}
                />
              )}
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(74, 159, 212, 0.22)" />
              <XAxis
                type="number"
                dataKey="temperature"
                domain={temperatureDomain}
                ticks={temperatureTicks}
                tick={{ fill: colors.textMuted, fontSize: 12 }}
                tickLine={{ stroke: colors.textMuted }}
                axisLine={{ stroke: "rgba(74, 159, 212, 0.4)" }}
                label={{
                  value: t("heatwave.perDegree.axisTemperature"),
                  position: "insideBottom",
                  offset: -12,
                  fill: colors.textMid,
                  fontSize: 13,
                }}
              />
              <YAxis
                type="number"
                dataKey="value"
                domain={yDomain}
                tick={{ fill: colors.textMuted, fontSize: 12 }}
                tickLine={{ stroke: colors.textMuted }}
                axisLine={{ stroke: "rgba(74, 159, 212, 0.4)" }}
                tickFormatter={(value) =>
                  Number(value).toLocaleString(i18n.language, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })
                }
                width={48}
                label={{
                  value: t("heatwave.perDegree.axisDelay"),
                  angle: -90,
                  position: "insideLeft",
                  fill: colors.textMid,
                  fontSize: 13,
                }}
              />
              <Line
                data={trendlineData}
                type="monotone"
                dataKey="value"
                stroke={colors.trendline}
                strokeWidth={2.5}
                dot={false}
                activeDot={false}
                legendType="none"
                tooltipType="none"
                isAnimationActive={visible}
                animationDuration={1200}
                style={{ pointerEvents: "none" }}
              />
              <Scatter
                name="days"
                data={scatterData}
                shape={renderScatterDot}
                isAnimationActive={visible}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
        {hoveredDot && (
          <FloatingTooltipShell
            key={hoveredDot.point.serviceDate}
            $x={hoveredDot.x}
            $y={hoveredDot.y}
          >
            <FloatingTooltipCard>
              <PointTooltipContent point={hoveredDot.point} />
            </FloatingTooltipCard>
          </FloatingTooltipShell>
        )}
      </ChartWrapper>
      <LegendRow>
        <LegendLine />
        <span>
          {t("heatwave.perDegree.trendline", {
            r2:
              trendlineR2 != null
                ? (trendlineR2 * 100).toLocaleString(i18n.language, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })
                : "—",
          })}
        </span>
      </LegendRow>
    </div>
  );
};

export default TemperatureDelayScatter;
